import React, { FC, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";

import { SpinnerView } from "@app/components";
import BillingTestCard from "@app/postBill/components/billingTestCard";
import BillingContactUs from "@app/postBill/components/billingContactUs";
import PaymentSummaryCard from "@app/postBill/components/paymentSummaryCard";
import { UPSAPIContext, UPSContext } from "@app/provider";
import { HEAP_EVENTS } from "@app/provider/types";
import { routes } from "@app/routing";
import {
  PaymentDueStatus,
  PaymentPlan,
  StatementStatus,
} from "@app/postBill/models";
import { PaymentPlanCard } from "@app/postBill/components/paymentPlanCard";
import { ButtonNoShadow } from "@app/components/buttonNoShadow";
import { BlueHeaderContent } from "@app/components/ui/layout";
import { LineItem } from "@app/ups/models";
import {
  CalculateInstallmentRequestData,
  CalculateInstallmentResponseData,
  CalculateLineItem,
  PaymentStatus,
} from "@app/ups/models/apiControllerModel";
import { convertFullTestName } from "@app/ups/utils/convertTestName";
import { logMsg } from "@app/utils/logMsg";
import {
  getFormattedMonthlyPaymentDate,
  getTestShortName,
} from "@app/postBill/utils";
import { renderCurrencyChar } from "@app/postBill/utils/renderCurrencyChar";
import { useUPSHeapEvent } from "@app/ups/hooks";

import "./upsReviewInvoice.scss";

const messages = defineMessages({
  upsReviewInvoiceTitle: {
    id: "upsReviewInvoiceTitle",
    defaultMessage: "Review invoice",
  },
  upsReivewInvoiceSubtitle: {
    id: "upsReivewInvoiceSubtitle",
    defaultMessage: `Click "Continue" at the bottom when you're ready.`,
  },
  upsReviewInvoiceContinueButton: {
    id: "upsReviewInvoiceContinueButton",
    defaultMessage: "Continue to pay {total} {testCount}",
  },
  upsReviewInvoiceSingleTestName: {
    id: "upsReviewInvoiceSingleTestName",
    defaultMessage: "test",
  },
  upsReviewInvoicePluralTestName: {
    id: "upsReviewInvoicePluralTestName",
    defaultMessage: "tests",
  },
  upsDynamicMonthlyPaymentDate: {
    id: "upsDynamicMonthlyPaymentDate",
    defaultMessage: "{day} of each month",
  },
});

const UPSReviewInvoice: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {
    upsApiProviderState: { guestValidationData, paymentRequestData },
    loadingStates,
    getPaymentRequest,
    calculateInstallment,
  } = useContext(UPSAPIContext);
  const {
    upsTotalAmount,
    setUPSTotalAmount,
    selectedUPSPaymentPlan,
    setSelectedUPSPaymentPlan,
    selectedUPSPaymentPlanItems,
    setSelectedUPSPaymentPlanItems,
  } = useContext(UPSContext);
  const { trackUPSHeapEvent } = useUPSHeapEvent();
  const [testCountText, setTestCountText] = useState<string>("");
  const [validStatus, setValidStatus] = useState<boolean>(false);
  const location = useLocation();

  const handleGetPaymentRequest = async () => {
    try {
      const response = await getPaymentRequest(
        guestValidationData?.paymentRequestId as string
      );
      if (
        response?.data?.status !== PaymentStatus.DRAFT &&
        response?.data?.status !== PaymentStatus.PROCESSING
      ) {
        navigate(routes.upsNoLongerAvailable);
      }
      setValidStatus(true);
      setUPSTotalAmount(response?.data?.totalAmountCents as number);
    } catch {
      logMsg("error", "ups.invoice", "Error on handleGetPaymentRequest");
    }
  };

  useEffect(() => {
    if (guestValidationData?.paymentRequestId) handleGetPaymentRequest();
  }, [guestValidationData?.paymentRequestId]);

  const handleContinueButton = async () => {
    trackUPSHeapEvent({
      eventKey: HEAP_EVENTS.ups_click_billing_payment_continuetopay,
    });
    navigate(routes.upsCreatePaymentMethod);
  };

  useEffect(() => {
    if (paymentRequestData?.lineItems?.length === 1)
      setTestCountText(
        `(1 ${intl.formatMessage(messages.upsReviewInvoiceSingleTestName)})`
      );
    if (
      paymentRequestData?.lineItems &&
      paymentRequestData?.lineItems?.length > 1
    )
      setTestCountText(
        `(${paymentRequestData?.lineItems?.length} ${intl.formatMessage(
          messages.upsReviewInvoicePluralTestName
        )})`
      );
  }, [paymentRequestData?.lineItems]);

  useEffect(() => {
    if (location.state?.from !== routes.upsCreatePaymentMethod) {
      setSelectedUPSPaymentPlan(PaymentPlan.None);
    }
  }, []);

  useEffect(() => {
    const fetchPaymentPlanCalculation = async () => {
      const hasPaymentPlanSelected =
        selectedUPSPaymentPlan !== PaymentPlan.None;
      const hasValidBills =
        (paymentRequestData?.lineItems?.length as number) > 0;

      if (!hasPaymentPlanSelected || !hasValidBills) {
        return;
      }

      const calculateInstallmentInput: CalculateInstallmentRequestData = {
        lineItems: paymentRequestData?.lineItems?.map((item) => ({
          name: item?.name,
          price: formatCentsToDollars(item?.amountCents),
        })) as CalculateLineItem[],
        numberOfInstallment: selectedUPSPaymentPlan,
      };

      try {
        const paymentPlanCalculation = await calculateInstallment(
          calculateInstallmentInput
        );
        const items =
          paymentPlanCalculation?.data?.map(
            (item: CalculateInstallmentResponseData) => ({
              ...item,
              amount: item?.amount * 10000,
            })
          ) ?? [];
        setSelectedUPSPaymentPlanItems(items);
      } catch {
        logMsg("error", "ups.invoice", "Error fetching payment plan preview");
      }
    };

    fetchPaymentPlanCalculation();
  }, [selectedUPSPaymentPlan, paymentRequestData?.lineItems, upsTotalAmount]);

  const formatCentsToDollars = (amountInCents: number): number => {
    return amountInCents / 100;
  };

  const getFirstPaymentDate = (): string => {
    return intl.formatMessage(messages.upsDynamicMonthlyPaymentDate, {
      day: getFormattedMonthlyPaymentDate(intl, new Date()),
    });
  };

  return (
    <>
      <SpinnerView isLoading={loadingStates.getPaymentRequest} />
      {validStatus && (
        <BlueHeaderContent
          blueHeaderTitle={intl.formatMessage(messages.upsReviewInvoiceTitle)}
          noGradient
        >
          <div className={"ups-review-invoice"}>
            <div className="ups-review-invoice__content">
              <div className="ups-review-invoice__subtitle">
                {intl.formatMessage(messages.upsReivewInvoiceSubtitle)}
              </div>
              <div className="ups-review-invoice__bills">
                {paymentRequestData?.lineItems?.map((item: LineItem) => (
                  <BillingTestCard
                    key={item?.lineItemUid}
                    testName={{
                      fullName: item?.name,
                      shortName: getTestShortName(item?.name),
                    }}
                    amount={item?.amountCents * 100}
                    caseNumber={item?.caseId}
                    dateOfService={item?.dateOfService}
                    dueDate={{
                      dueDate: item?.expirationDate,
                      pastDueExists: PaymentDueStatus.VALID_DATE,
                    }}
                    orderedBy={item?.orderedBy}
                    statementStatus={StatementStatus.READY_TO_PAY}
                    currency={paymentRequestData?.currency}
                    ups
                  />
                ))}
              </div>

              {paymentRequestData?.enablePaymentPlan && (
                <PaymentPlanCard
                  selectedPlan={selectedUPSPaymentPlan}
                  onSelectPlan={setSelectedUPSPaymentPlan}
                  onRemovePlan={() =>
                    setSelectedUPSPaymentPlan(PaymentPlan.None)
                  }
                />
              )}
              {(paymentRequestData?.lineItems?.length as number) > 0 && (
                <PaymentSummaryCard
                  selectedPlan={selectedUPSPaymentPlan}
                  testNames={
                    paymentRequestData?.lineItems?.map(
                      (item: LineItem) =>
                        convertFullTestName(item?.name) as string
                    ) as string[]
                  }
                  testPrices={
                    paymentRequestData?.lineItems?.map(
                      (item: LineItem) => item?.amountCents * 100
                    ) as number[]
                  }
                  paymentItems={selectedUPSPaymentPlanItems}
                  totalPrice={
                    (paymentRequestData?.totalAmountCents as number) * 100
                  }
                  currency={paymentRequestData?.currency as string}
                  firstPaymentDate={getFirstPaymentDate()}
                />
              )}

              <div className="ups-review-invoice__button">
                <ButtonNoShadow
                  raised
                  onClick={handleContinueButton}
                  className="ups-review-invoice__continue-button"
                >
                  {intl.formatMessage(messages.upsReviewInvoiceContinueButton, {
                    total: paymentRequestData?.totalAmountCents
                      ? `${renderCurrencyChar(paymentRequestData?.currency)}${formatCentsToDollars(
                          paymentRequestData?.totalAmountCents as number
                        )}`
                      : "",
                    testCount: testCountText,
                  })}
                </ButtonNoShadow>
              </div>
              <BillingContactUs />
            </div>
          </div>
        </BlueHeaderContent>
      )}
    </>
  );
};

export default UPSReviewInvoice;
